import React from 'react';
import { toast } from 'sonner';
import { Loader2 } from 'lucide-react';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '../../ui/dialog';
import { Button } from '../../ui/button';
import { adminApi } from '../../../lib/adminAuth';
import { errMsg } from './docUtils';

export default function ConvertDialog({ proposal, onClose, onDone }) {
  const [categories, setCategories] = React.useState([]);
  const [departments, setDepartments] = React.useState([]);
  const [category, setCategory] = React.useState('');
  const [depts, setDepts] = React.useState([]);
  const [busy, setBusy] = React.useState(false);
  const isProspect = proposal && !proposal.client_id;

  React.useEffect(() => {
    if (!proposal || !isProspect) return;
    adminApi.get('/api/admin/client-categories').then((r) => { setCategories(r.data || []); setCategory((r.data || [])[0]?.name || ''); }).catch(() => {});
    adminApi.get('/api/departments').then((r) => setDepartments((Array.isArray(r.data) ? r.data : r.data?.departments || []).map((d) => d.name || d))).catch(() => {});
  }, [proposal, isProspect]);

  const convert = async () => {
    setBusy(true);
    try {
      const r = await adminApi.post(`/api/admin/proposals/${proposal._id}/convert`, { category: category || null, departments: depts });
      toast.success(`Converted · ${r.data.contract_number} drafted`);
      onDone(r.data);
    } catch (e) { toast.error(errMsg(e, 'Could not convert')); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={!!proposal} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" data-testid="convert-dialog">
        <DialogHeader><DialogTitle>Convert to {isProspect ? 'client + contract' : 'contract'}</DialogTitle></DialogHeader>
        {proposal && !['accepted'].includes(proposal.effective_status) && (
          <p className="text-xs p-2.5 rounded-lg bg-amber-50 text-amber-800" data-testid="convert-not-accepted">This proposal has not been accepted online. Convert only if the client agreed another way.</p>
        )}
        <p className="text-sm text-slate-600">
          {isProspect ? `A new client "${proposal?.brand_name || proposal?.recipient_company}" is added with the proposal's contact and fee, ` : 'The client is kept as is, '}
          and a draft contract is created with the proposal&apos;s scope. Review it before sending for signature.
        </p>
        {isProspect && (
          <div className="space-y-3">
            <div>
              <label className="text-xs font-semibold text-slate-600">Client category</label>
              <select value={category} onChange={(e) => setCategory(e.target.value)} className="w-full h-10 px-3 rounded-lg border border-slate-200 text-sm bg-white" data-testid="convert-category">
                {categories.map((c) => <option key={c._id} value={c.name}>{c.name}</option>)}
              </select>
            </div>
            {departments.length > 0 && (
              <div>
                <label className="text-xs font-semibold text-slate-600">Departments working on this client</label>
                <div className="flex flex-wrap gap-1.5 mt-1">
                  {departments.map((d) => (
                    <button key={d} type="button" onClick={() => setDepts((x) => (x.includes(d) ? x.filter((y) => y !== d) : [...x, d]))} data-testid={`convert-dept-${d}`}
                      className={`px-2.5 py-1 rounded-full text-xs border ${depts.includes(d) ? 'bg-fuchsia-600 text-white border-fuchsia-600' : 'bg-white border-slate-200 text-slate-600'}`}>{d}</button>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
        <DialogFooter>
          <Button onClick={convert} disabled={busy} className="bg-gray-900 hover:bg-gray-800" data-testid="convert-submit">
            {busy && <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />} Convert
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
